import type { AxiosResponse } from 'axios';

import {
  DATALINE_RESPONSE_DRIVER_MINIMUM,
  DatalineStatus,
} from 'src/constants/dataline';
import { API_RESULT_LIMITS } from 'src/constants/settings';
import type { DatalineUser, OriginalDataline } from 'src/types/dataline';
import type { Report } from 'src/types/report';
import type { DatalineReturnResponse } from 'src/types/workspace';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import {
  convertFrontendToBackendDatalines,
  findDataPosition,
} from 'src/components/workspace/helper/helper';

interface PaginationStatus {
  offset: number;
  count: number;
  hasNextPage: boolean;
}

interface CustomReportsResult {
  customReports: Report[] | null;
  paginationStatus: PaginationStatus;
  error?: any;
}

interface DatalineTemplatesResult {
  datalineTemplates: DatalineUser[] | null;
  paginationStatus: PaginationStatus;
  error?: any;
}

interface DatalineReturnsResult {
  updatedDatalines: OriginalDataline[];
  error?: any;
}

export const getCustomReportsHelper = async (
  currentReports: Report[] | null,
  currentPaginationStatus: PaginationStatus,
  options: {
    reset?: boolean;
    sharedOnly?: boolean;
    searchUrl?: string;
    filters?: Record<string, any>;
  },
): Promise<CustomReportsResult> => {
  const { reset, sharedOnly, searchUrl, filters } = options;
  const requestEndpoint =
    searchUrl ||
    (sharedOnly
      ? apiPaths.GET_CUSTOM_REPORTS_SHARED
      : apiPaths.GET_CUSTOM_REPORTS);

  try {
    const response = await HttpService.get(
      requestEndpoint,
      {
        offset: reset ? 0 : currentPaginationStatus.offset,
        ...(filters ? filters : {}),
      },
      true,
    );

    const { data: customReportsPagination } = response;
    const { results: newCustomReports } = customReportsPagination;
    const newPaginationStatus = { ...currentPaginationStatus };

    if (reset) newPaginationStatus.offset = 0;

    newPaginationStatus.offset += API_RESULT_LIMITS.DEFAULT;
    newPaginationStatus.count = customReportsPagination.count;
    newPaginationStatus.hasNextPage =
      newPaginationStatus.offset < newPaginationStatus.count;

    return {
      customReports:
        currentReports && !reset
          ? currentReports.concat(newCustomReports)
          : newCustomReports,
      paginationStatus: newPaginationStatus,
    };
  } catch (error: any) {
    return {
      customReports: currentReports,
      paginationStatus: currentPaginationStatus,
      error: error.response,
    };
  }
};

export const getDatalineTemplatesHelper = async (
  currentTemplates: DatalineUser[] | null,
  currentPaginationStatus: PaginationStatus,
  reset = false,
): Promise<DatalineTemplatesResult> => {
  const requestEndpoint = apiPaths.TEMPLATE;

  try {
    const response: AxiosResponse<{
      results: DatalineUser[];
      count: number;
    }> = await HttpService.get(
      requestEndpoint,
      { offset: reset ? 0 : currentPaginationStatus.offset },
      true,
    );
    const { data: datalineTemplatesPagination } = response;
    const { results: newDatalineTemplates } = datalineTemplatesPagination;
    const newPaginationStatus = { ...currentPaginationStatus };

    if (reset) newPaginationStatus.offset = 0;

    newPaginationStatus.offset += API_RESULT_LIMITS.DEFAULT;
    newPaginationStatus.count = datalineTemplatesPagination.count;
    newPaginationStatus.hasNextPage =
      newPaginationStatus.offset < newPaginationStatus.count;

    return {
      datalineTemplates:
        currentTemplates && !reset
          ? currentTemplates.concat(newDatalineTemplates)
          : newDatalineTemplates,
      paginationStatus: newPaginationStatus,
    };
  } catch (error: any) {
    return {
      datalineTemplates: currentTemplates,
      paginationStatus: currentPaginationStatus,
      error: error.response,
    };
  }
};

export const getDatalineReturnsHelper = async (
  datalinesToCheck: OriginalDataline[],
  currentDatalines: OriginalDataline[],
): Promise<DatalineReturnsResult> => {
  // since the reporting engine cannot calculate returns for external
  // datalines, these need to be filtered out
  const requestableDatalines = datalinesToCheck.filter(
    (dataline) => !dataline.isExternal,
  );
  if (requestableDatalines.length === 0) {
    return { updatedDatalines: currentDatalines };
  }

  const requestEndpoint = apiPaths.GET_DATALINE_RETURNS;
  const convertedDatalines = convertFrontendToBackendDatalines(
    requestableDatalines,
  ).map((dl) => dl.dataline.data);
  const renderIds = requestableDatalines.map((dataline) => dataline.renderId);

  try {
    const response = await HttpService.post(requestEndpoint, {
      data: convertedDatalines,
    });
    const datalines = [...currentDatalines];

    response.data.datalines.forEach(
      ({ returns, status }: DatalineReturnResponse, index: number) => {
        const renderId = String(renderIds[index]);

        if (!Object.values(DatalineStatus).includes(status)) {
          throw new Error('Something went wrong while fetching the returns.');
        }

        const { datalineIndex } = findDataPosition(datalines, renderId);
        if (datalineIndex === null) return;

        // driverAnalysis can only remain selected if there are >30 returns
        const driverAnalysis =
          datalines[datalineIndex].driverAnalysis &&
          status === DatalineStatus.STATUS_OK &&
          returns >= DATALINE_RESPONSE_DRIVER_MINIMUM;
        datalines[datalineIndex] = {
          ...datalines[datalineIndex],
          returns,
          status,
          driverAnalysis,
        };
      },
    );

    return { updatedDatalines: datalines };
  } catch (error: any) {
    const datalinesWithErrorStatus = currentDatalines.map((dataline) => {
      if (renderIds.includes(dataline.renderId)) {
        return {
          ...dataline,
          status: DatalineStatus.STATUS_ERROR,
          returns: null,
        };
      }

      return dataline;
    });

    return {
      updatedDatalines: datalinesWithErrorStatus,
      error: error.response ?? 'REQUEST_FAILED',
    };
  }
};
